/**
 * The session and device ids a guest's browser sends with every public gift
 * card request, so the API's enumeration controls (spec 2026-10-08 §5.1) can
 * count misses per session and per device. Random, not personal: the API only
 * ever stores a salted hash of each.
 *
 * Session: per tab (sessionStorage). Device: per browser (localStorage). Both
 * fall back to an in-memory value when storage is unavailable (private mode),
 * which only makes the guest look like a fresh session — the IP still counts.
 */

const SESSION_KEY = 'ou.gc.session';
const DEVICE_KEY = 'ou.gc.device';

let memorySession: string | null = null;
let memoryDevice: string | null = null;

function randomId(): string {
    const bytes = new Uint8Array(18);
    crypto.getRandomValues(bytes);
    return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
}

function fromStorage(storage: () => Storage, key: string): string | null {
    try {
        const s = storage();
        const existing = s.getItem(key);
        if (existing && /^[A-Za-z0-9_-]{16,64}$/.test(existing)) return existing;
        const fresh = randomId();
        s.setItem(key, fresh);
        return fresh;
    } catch {
        return null;
    }
}

export function guestProbeIds(): { sessionId: string; deviceId: string } {
    const sessionId = fromStorage(() => window.sessionStorage, SESSION_KEY) ?? (memorySession ??= randomId());
    const deviceId = fromStorage(() => window.localStorage, DEVICE_KEY) ?? (memoryDevice ??= randomId());
    return { sessionId, deviceId };
}
